import get from "lodash/get";

import { Button, Form, Header, Icon, Message } from "semantic-ui-react";

import React, { useState } from "react";
import { useTranslation, withTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import distConfig from "astrid-config/src/distributors";
import { db } from "astrid-firebase";
import { getDistOptions } from "astrid-web/src/components/production/fnc";
import { durationToSec, secToDuration } from "astrid-web/src/helpers/fnc";

import featureFlags from "../../../features/authorization/constants/featureFlags";
import useHasRight from "../../../features/authorization/hooks/useHasRight";

import FlexColumn from "../../Flex/FlexColumn";

import MigrateProduction from "./MigrateProduction";

const getActiveArticles = (production) => {
	const activeArticles = [];

	if (typeof production.isbn === "string") activeArticles.push("total");
	if (production.deliveryEbook) activeArticles.push("ebook");
	if (production.deliveryParts) activeArticles.push("part");
	if (production.deliveryCD) activeArticles.push("cd");
	if (production.deliveryMP3CD) activeArticles.push("mp3cd");

	return activeArticles;
};

const ProductionDelivery = ({
	productionId,
	production,
	totalPartDuration,
	deliveryDuration,
	publisher,
	updateStatus,
	setParentState,
}) => {
	const { t } = useTranslation();
	const activeArticles = getActiveArticles(production);
	const distOptions = getDistOptions({ production, publisher });
	const [showMigrateProductionModal, setShowMigrateProductionModal] = useState(false);
	const hasRight = useHasRight();
	const hasFlattening = publisher?.featureFlags?.includes(featureFlags.FLATTENING);
	const canFinalize = production.work || hasFlattening || !publisher?.distributionOptions;

	// show stoppers
	const deliveryProblems = [];

	if (activeArticles.includes("total")) {
		if (!(deliveryDuration !== null || production.deliveryDuration)) deliveryProblems.push(t("specifyTotDuration"));

		if (
			!get(production, "master.total.settings.approved") &&
			production?.distributionOptions?.settings?.total?.storytoolWav?.part !== "parts"
		)
			deliveryProblems.push(t("uploadMasterAndSaveExp"));
	}

	if (activeArticles.includes("part")) {
		if (production.deliveryParts.find((part, i) => !part.duration))
			deliveryProblems.push(t("specifyDurationForParts"));

		if (production.deliveryParts.find((part, i) => !get(production, "master.part_" + i + ".settings.approved")))
			deliveryProblems.push(t("uploadMasterAndSaveExpForEachPart"));
	}

	if (activeArticles.includes("cd")) {
		if (!get(production, "master.cd.settings.approved")) deliveryProblems.push(t("uploadIntroAndSaveCdExports"));

		const requireIFCD = distOptions && distOptions.cd && distOptions.cd.find((dist) => distConfig[dist].ifcd);
		if (requireIFCD && !get(production, "deliveryCD.ifcd")) deliveryProblems.push(t("specifyIfcdCdNr"));
	}

	if (activeArticles.includes("mp3cd")) {
		if (!get(production, "master.mp3cd.settings.approved")) deliveryProblems.push(t("saveExportsForMp3"));

		const requireIFCD = distOptions && distOptions.mp3cd && distOptions.mp3cd.find((dist) => distConfig[dist].ifcd);
		if (requireIFCD && !get(production, "deliveryMP3CD.ifcd")) deliveryProblems.push(t("specifyIfcdNrForMp3"));
	}

	const prooferProgress = production?.proofer?.includes("NONE")
		? false
		: production.progress?.proofed?.time ||
		  production.progress?.proofed?.[
				production.proofer?.[0] ||
					(production.progress.proofed ? Object.keys(production.progress.proofed)[0] : null)
		  ];

	const handleUpload = () => {
		// legacy delivery, just change the status
		updateStatus("done");
		window.scrollTo(0, 0);
		window.alert(t("legacyUploadWarning"));
	};

	const checkReminderComments = async () => {
		let comments = await db.collection("productions").doc(productionId).collection("comments").get();
		let reminderComments = [];
		for await (const doc of comments.docs) {
			const d = doc.data();
			if (d.completionReminder) reminderComments.push(d.text);
		}
		reminderComments.forEach((comment) => window.alert(t("reminderCommentWarning", { comment })));
		return true;
	};

	const finish = () => {
		checkReminderComments().then(() => {
			if (
				(!production.stats ||
					production.productionType === "subcontract" ||
					(production?.stats?.pages && production?.stats?.pages?.total >= production?.pages)) &&
				window.confirm(t("confirmUpload"))
			) {
				handleUpload();
			} else if (
				(!production?.stats?.pages || production?.stats?.pages?.total < production.pages) &&
				window.confirm(t("allPagesNotDone"))
			) {
				handleUpload();
			}
		});
	};

	return (
		<>
			<Form as="div">
				<Header as="h4" icon="circle check outline" content={t("finishProd")} />
				{!production.work && (
					<>
						{production.deliveryParts &&
						(!production.isbn ||
							(production.deliveryDuration &&
								totalPartDuration > durationToSec(production.deliveryDuration))) ? (
							<p>
								<b>
									{t("finalTotDuration")}{" "}
									{totalPartDuration ? secToDuration(totalPartDuration) : t("notSpecified")}
								</b>
							</p>
						) : (
							<Form.Input
								pattern="([0-9]{0,3})(:[0-5][0-9]){2}"
								value={deliveryDuration !== null ? deliveryDuration : production.deliveryDuration || ""}
								onChange={(e, data) => {
									setParentState({ deliveryDuration: data.value });
								}}
								label={t("finalTotalDuration")}
								placeholder={t("timerStart")}
								required
							/>
						)}

						{prooferProgress && (
							<p style={{ marginTop: "-1em" }}>
								<small>
									{t("prooferProgress")} {secToDuration(prooferProgress / 1000, false)}
								</small>
							</p>
						)}

						<Form.TextArea id="deliveryComment" label={t("infoAboutDel")} />

						{!production.work && !!deliveryProblems.length && (
							<div>
								<b>{t("fixBeforeDelivery")}</b>
								<ul className="production-delivery-problems">
									{deliveryProblems.sort().map((problem, i) => (
										<li key={i}>{problem}</li>
									))}
								</ul>
							</div>
						)}
					</>
				)}
				{hasRight("productions.finalize") && canFinalize && (
					<>
						{!production.work ? (
							<Button
								content={t("finalizeProd")}
								icon="right arrow"
								color="green"
								disabled={!!deliveryProblems.length}
								labelPosition="right"
								onClick={(e) => {
									if (hasFlattening) {
										checkReminderComments().then(() => {
											if (
												(!production.stats ||
													production.productionType === "subcontract" ||
													(production?.stats?.pages &&
														production?.stats?.pages?.total >= production?.pages)) &&
												window.confirm(t("confirmUpload"))
											) {
												setShowMigrateProductionModal(true);
											} else if (
												(!production?.stats?.pages ||
													production?.stats?.pages?.total < production.pages) &&
												window.confirm(t("allPagesNotDone"))
											) {
												setShowMigrateProductionModal(true);
											}
										});
									} else {
										finish();
									}
								}}
							/>
						) : (
							<Message
								info
								content={
									<FlexColumn style={{ gap: 10 }}>
										{t(
											"finalizeMigratedProduction",
											"This production is migrated. Finialize it by delivering the files.",
										)}
										<Button primary icon labelPosition="right" as={Link} to={`../artifacts`}>
											{t("delivery", "Delivery")}
											<Icon name="right arrow" style={{ background: "none" }} />
										</Button>
									</FlexColumn>
								}
							/>
						)}
					</>
				)}
			</Form>
			{showMigrateProductionModal && (
				<MigrateProduction
					production={production}
					setMigrateProductionOpen={setShowMigrateProductionModal}
					updateProductionStatusCallback={() => updateStatus("done")}
					isFinalizing={true}
				/>
			)}
		</>
	);
};

export default withTranslation()(ProductionDelivery);
